import {
  AuthForm,
  AuthFormFooter,
  CheckBox,
  CheckField,
  ErrorMessage,
  FieldWrapper,
  Input,
  InputIcon,
  Remember,
  Signup,
  SignUpButton,
  SubTitle,
  Title,
  UnderLineLink,
} from '@Components/Auth/Auth.styled';
import { Spinner } from '@Components/common/Spinner';
import { COOKIE_GUEST, COOKIE_GUEST_REFRESH } from '@Constants/index';
import { useAuth } from '@Contexts/AuthContext/Auth.context';
import { _GetUserProfile, _Login } from '@Services/Authentication';
import { AxiosError } from 'axios';
import useTranslation from 'next-translate/useTranslation';
import Image from 'next/image';
import Hide from 'public/hide.svg';
import Logo from 'public/logo.svg';
import Show from 'public/show.svg';
import { memo, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useCookie } from 'react-use';
import { LoginFormValues } from 'types/User';
type Props = {};

interface LoginForm extends LoginFormValues {
  remember: boolean;
}

export default memo(function LoginForm({}: Props) {
  const { t: tAuth } = useTranslation('Auth');
  const [show, setShow] = useState<Boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { setUserData, updateToken } = useAuth();
  const [submitHasError, setSubmitHasError] = useState<string>('');
  const [, , deleteCookieGuestToken] = useCookie(COOKIE_GUEST);
  const [, , deleteCookieGuestRefreshToken] = useCookie(COOKIE_GUEST_REFRESH);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>();

  const onSubmitHandler: SubmitHandler<LoginForm> = ({ remember, ...data }) => {
    setIsLoading(true);
    setSubmitHasError('');
    _Login({
      ...data,
      username: data.username.toLowerCase(),
    })
      .then(async ({ data }) => {
        // if (remember) {
        //   updateToken(data.access, data.refresh, true);
        // } else {
        //   configureAxiosAuth(data.access);
        // }

        updateToken(data.access, data.refresh, true);
        await _GetUserProfile(data.access)
          .then(({ data }) => {
            setUserData(data);
          })
          .catch((err: AxiosError) => {
            console.log('Error Login: ', err.response?.data);
          });
      })
      .catch((err: AxiosError) => {
        setIsLoading(false);
        setSubmitHasError(err.response?.data?.message || err.response?.data?.detail || '');
      })
      .finally(() => {
        deleteCookieGuestToken();
        deleteCookieGuestRefreshToken();
      });
  };

  return (
    <AuthForm onSubmit={handleSubmit(onSubmitHandler)}>
      <Image src={Logo} width={235.61} height={33.02} alt="auth-logo" />
      <Title>{tAuth('login_title')}</Title>
      <SubTitle>{tAuth('login_subtitle')}</SubTitle>
      <FieldWrapper>
        <Input
          disabled={isLoading}
          type="text"
          placeholder={tAuth('email_phone')}
          {...register('username', { required: true })}
        />
        <ErrorMessage>{errors.username?.type === 'required' && tAuth('email_or_phone_error')}</ErrorMessage>
      </FieldWrapper>
      <FieldWrapper>
        <Input
          type={show ? 'text' : 'password'}
          hasIcon
          disabled={isLoading}
          placeholder={tAuth('password')}
          {...register('password', { required: true, minLength: 8 })}
        />
        <InputIcon>
          <Image
            src={show ? Hide : Show}
            width={24}
            height={14.64}
            alt="password_icon"
            onClick={() => setShow((prev) => !prev)}
          />
        </InputIcon>
        {Boolean(errors.password) &&
          (errors.password?.type === 'required' ? tAuth('password_error') : tAuth('password_minlength'))}
      </FieldWrapper>
      <SignUpButton variant="filled-red" type="submit" disabled={isLoading}>
        {isLoading ? <Spinner /> : tAuth('sign_in')}
      </SignUpButton>
      {submitHasError && <ErrorMessage>{submitHasError}</ErrorMessage>}
      <Remember>
        <CheckField htmlFor="remember">
          <input disabled={isLoading} type="checkbox" id="remember" {...register('remember')} />
          <CheckBox></CheckBox> {tAuth('remember')}
        </CheckField>
        <UnderLineLink href="/auth/forgot-password">{tAuth('forget')}</UnderLineLink>
      </Remember>
      <AuthFormFooter>
        <p>{tAuth('no_account')}</p>
        &nbsp;
        <Signup href="/auth/register">{tAuth('register_now')}</Signup>
      </AuthFormFooter>
    </AuthForm>
  );
});
